"use client";

import { useState, useEffect, useRef, memo } from "react";
import ProductImageViewer, { type ViewerOrigin } from "./ProductImageViewer";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { Package as PackageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import type { ProductImage } from "@/types";
import { useTranslations } from "next-intl";

interface ProductImageGalleryProps {
  images: ProductImage[];
  productName: string;
}

function ProductImageGallery({
  images,
  productName,
}: ProductImageGalleryProps) {
  // 사진 뷰어 — 누른 사진부터 연다(null = 닫힘).
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  // Hero 전환 출발점(누른 사진의 위치·이미 받은 파일)
  const [viewerOrigin, setViewerOrigin] = useState<ViewerOrigin | null>(null);
  const desktopRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  // 모바일 캐러셀의 지금 사진 — "사진 모두 보기"를 누르면 이 사진부터 연다.
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const t = useTranslations("product.gallery");
  const displayImages = images;

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setCurrentSlide(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  /**
   * 누른 사진(또는 버튼이면 그 사진 칸)에서 뷰어를 연다. 사진이 이미 로드돼 있으면
   * 그 위치·파일을 넘겨 Hero 전환을 하고, 아니면 페이드로만 연다.
   */
  const openViewer = (index: number, view: "desktop" | "mobile") => {
    const root = view === "desktop" ? desktopRef.current : mobileRef.current;
    const box = root?.querySelector<HTMLElement>(
      `[data-photo-index="${index}"]`
    );
    const img = box?.querySelector("img");
    setViewerOrigin(
      box && img && img.complete && img.naturalWidth > 0
        ? {
            rect: box.getBoundingClientRect(),
            src: img.currentSrc || img.src,
            ratio: img.naturalWidth / img.naturalHeight,
          }
        : null
    );
    setViewerIndex(index);
  };

  return (
    <>
      {/* Desktop Grid Layout */}
      <div
        ref={desktopRef}
        className="hidden md:block max-w-7xl mx-auto px-6 lg:px-20 xl:px-24 md:pt-6"
      >
        <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden relative">
          {displayImages.length > 0 ? (
            <>
              {/* Main large image - Left (2/3 width) */}
              <div
                className="col-span-2 row-span-2 relative group cursor-pointer aspect-square"
                data-photo-index={0}
                onClick={() => openViewer(0, "desktop")}
              >
                <OptimizedImage
                  src={displayImages[0].image_url}
                  alt={productName}
                  fill
                  priority
                  fetchPriority="high"
                  className="object-cover hover:brightness-95 transition"
                  sizes="(max-width: 768px) 100vw, 66vw"
                />
              </div>

              {/* Right column with 2 smaller images (1/3 width each) */}
              {displayImages.slice(1, 3).map((img, idx) => (
                <div
                  key={img.id}
                  className="relative group cursor-pointer aspect-square"
                  data-photo-index={idx + 1}
                  onClick={() => openViewer(idx + 1, "desktop")}
                >
                  <OptimizedImage
                    src={img.image_url}
                    alt={`${productName} ${idx + 2}`}
                    fill
                    className="object-cover hover:brightness-95 transition"
                    sizes="(max-width: 768px) 50vw, 33vw"
                  />
                </div>
              ))}

              {/* Show all photos button - Desktop */}
              {displayImages.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openViewer(0, "desktop")}
                  className="absolute bottom-4 right-4 bg-white border-gray-700 text-sm shadow-md z-10"
                >
                  {t("viewAllPhotos")}
                </Button>
              )}
            </>
          ) : (
            <div className="col-span-3 bg-gray-100 flex items-center justify-center aspect-square">
              <PackageIcon className="h-24 w-24 text-gray-400" />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Slider */}
      <div ref={mobileRef} className="md:hidden relative">
        <Carousel
          setApi={setCarouselApi}
          className="w-full"
          opts={{
            loop: false,
          }}
        >
          <CarouselContent>
            {displayImages.length > 0 ? (
              displayImages.map((img, idx) => (
                <CarouselItem key={img.id}>
                  <div
                    className="relative w-full aspect-square cursor-zoom-in"
                    data-photo-index={idx}
                    onClick={() => openViewer(idx, "mobile")}
                  >
                    <OptimizedImage
                      src={img.image_url}
                      alt={`${productName} ${idx + 1}`}
                      fill
                      priority={idx === 0}
                      fetchPriority={idx === 0 ? "high" : "auto"}
                      className="object-cover"
                      sizes="100vw"
                    />
                  </div>
                </CarouselItem>
              ))
            ) : (
              <CarouselItem>
                <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                  <PackageIcon className="h-24 w-24 text-gray-400" />
                </div>
              </CarouselItem>
            )}
          </CarouselContent>
          {displayImages.length > 1 && (
            <>
              <CarouselPrevious className="left-2" />
              <CarouselNext className="right-2" />
            </>
          )}
        </Carousel>

        {/* Show all photos button */}
        {displayImages.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openViewer(currentSlide, "mobile")}
            className="absolute bottom-2 right-2 bg-white border-gray-700 text-sm shadow-md z-10"
          >
            {t("viewAllPhotos")}
          </Button>
        )}
      </div>

      <ProductImageViewer
        images={displayImages}
        productName={productName}
        open={viewerIndex !== null}
        startIndex={viewerIndex ?? 0}
        origin={viewerOrigin}
        onClose={() => setViewerIndex(null)}
      />
    </>
  );
}

export default memo(ProductImageGallery);
